import dayjs, {
  type Dayjs,
  formatDisplayDate,
  formatWeekday,
} from '@schuelerhilfe/shared/src/utils/dayjs';
import * as Sentry from '@sentry/nextjs';
import { SentryMessageCategory, withSentryCategory } from '@/features/sentry';
import { fetchClient } from '@/lib/apiClient';

const MAX_SLOT_DAYS = 12;

export enum TimeSlotMode {
  Location = 'location',
  Phone = 'phone',
}

export interface AppointmentDay {
  day: string;
  date: string;
  dateIso: string;
  timesLocation: string[];
  timesPhone: string[];
}

function transformSlots(
  slots: {
    phoneAppointment: boolean;
    day: string;
    date: string;
    times: string[];
  }[],
): AppointmentDay[] {
  const byDate = new Map<string, AppointmentDay>();
  for (const slot of slots) {
    let entry = byDate.get(slot.date);
    if (!entry) {
      entry = {
        day: formatWeekday(slot.date),
        date: formatDisplayDate(slot.date),
        dateIso: slot.date,
        timesLocation: [],
        timesPhone: [],
      };
      byDate.set(slot.date, entry);
    }
    if (slot.phoneAppointment) {
      entry.timesPhone.push(...slot.times);
    } else {
      entry.timesLocation.push(...slot.times);
    }
  }
  return [...byDate.values()]
    .filter(
      ({ timesLocation, timesPhone }) =>
        timesLocation.length > 0 || timesPhone.length > 0,
    )
    .sort((a, b) => a.dateIso.localeCompare(b.dateIso))
    .slice(0, MAX_SLOT_DAYS);
}

const DEFAULT_TIMES = [
  '14:00',
  '14:30',
  '15:00',
  '15:30',
  '16:00',
  '16:30',
  '17:00',
  '17:30',
] as const;
const DEFAULT_SLOT_COUNT = 5;

export function generateDefaultSlots(
  fromDate?: Date,
  now: Dayjs = dayjs(),
): AppointmentDay[] {
  const days: AppointmentDay[] = [];
  const filterByTime = fromDate === undefined;
  let cursor =
    fromDate !== undefined
      ? dayjs(fromDate).startOf('day')
      : now.startOf('day');

  while (days.length < DEFAULT_SLOT_COUNT) {
    const isWeekend = cursor.day() === 0 || cursor.day() === 6;
    if (!isWeekend) {
      const isToday = filterByTime && cursor.isSame(now, 'day');
      const times = isToday
        ? [...DEFAULT_TIMES].filter((t) => {
            const [h, m] = t.split(':').map(Number);
            return now.hour() < h || (now.hour() === h && now.minute() <= m);
          })
        : [...DEFAULT_TIMES];

      if (times.length > 0) {
        const dateIso = cursor.format('YYYY-MM-DD');
        days.push({
          day: formatWeekday(dateIso),
          date: formatDisplayDate(dateIso),
          dateIso,
          timesLocation: times,
          timesPhone: [],
        });
      }
    }
    cursor = cursor.add(1, 'day');
  }

  return days;
}

/**
 * Why a Standort's slots could not be shown.
 *
 * The old `AppointmentDay[] | null` return collapsed two unrelated outcomes
 * into the same `null`: "this Standort has no bookable slots" (expected, and
 * the common case) and "the slots API is broken" (an incident). Both produced
 * the identical Sentry warning, so the signal could never distinguish a quiet
 * Tuesday from an outage.
 */
export type AppointmentSlotsResult =
  /** The API answered with at least one renderable day. */
  | { kind: 'slots'; days: AppointmentDay[] }
  /** The API answered fine, with nothing bookable — nothing is wrong. */
  | { kind: 'empty' }
  /** The request failed. `status` is absent for a network-level failure. */
  | { kind: 'error'; status: number | undefined; cause: unknown };

export async function getAppointmentSlots(
  slug: string,
): Promise<AppointmentSlotsResult> {
  let result;
  try {
    result = await fetchClient.GET('/api/locations/{slug}/appointments/slots', {
      params: { path: { slug } },
    });
  } catch (cause) {
    // openapi-fetch only throws for a network-level failure; an HTTP error
    // comes back as a resolved `{ error }`.
    return { kind: 'error', status: undefined, cause };
  }

  const { data, error, response } = result;
  if (error !== undefined || !response.ok) {
    return { kind: 'error', status: response.status, cause: error };
  }
  if (!data) {
    return { kind: 'empty' };
  }

  // A Standort with nothing bookable is `empty`, not `slots`, whether the
  // backend says so with no body or with `200 []` — and `transformSlots` can
  // also reduce a non-empty upstream list to zero renderable days. All three
  // mean the same thing to a visitor, and all three should reach the default
  // timeslots rather than an empty day picker.
  let days: AppointmentDay[];
  try {
    days = transformSlots(data);
  } catch (cause) {
    // The backend passes the SH gateway's array through verbatim, without
    // validating each element ("resilient to additive gateway changes" —
    // AppointmentsSlotsService.getSlots), so a malformed element reaches
    // `transformSlots` and throws (e.g. `slot.times is not iterable`).
    // Treating that as an error rather than letting it reject is what keeps
    // the widgets out of a permanent loading state — see `loadAppointmentSlots`.
    return { kind: 'error', status: response.status, cause };
  }
  return days.length > 0 ? { kind: 'slots', days } : { kind: 'empty' };
}

/**
 * Fetches a Standort's slots for the booking widgets, reporting only genuine
 * failures, and returns `null` when the caller should fall back to
 * {@link generateDefaultSlots}.
 *
 * Shared by all three booking widgets so the instrumentation cannot drift:
 * previously only `BookAppointmentWidget` reported the fallback at all, and
 * the two modal variants took the same path silently.
 *
 * **Never rejects.** The widgets dispatch from a bare `.then()` with no
 * `.catch()`, so a rejection would leave `slotsStatus` on `Loading` forever
 * and render an empty day picker — the one outcome the default-timeslot
 * fallback exists to prevent. Every failure resolves to `null` instead, which
 * is the widgets' signal to render {@link generateDefaultSlots}.
 */
export async function loadAppointmentSlots(
  slug: string,
): Promise<AppointmentDay[] | null> {
  const result = await getAppointmentSlots(slug);

  if (result.kind === 'error') {
    // The slug is a tag, never part of the message: interpolating it made
    // Sentry file a separate issue per Standort, so one broken endpoint
    // arrived as a dozen unrelated-looking issues.
    try {
      Sentry.captureException(
        new Error(
          withSentryCategory(
            SentryMessageCategory.BOOKING_FALLBACK,
            'Appointment slots request failed; falling back to default timeslots',
          ),
          { cause: result.cause },
        ),
        {
          tags: {
            locationSlug: slug,
            status: String(result.status ?? 'network'),
          },
        },
      );
    } catch {
      // Reporting must never be the reason a visitor sees an empty widget.
    }
  }

  // `kind: 'empty'` is deliberately silent — a Standort with no bookable slots
  // is an ordinary business state, not an error, and reporting it produced one
  // Sentry issue per Standort per visitor.
  //
  // Both non-`slots` outcomes return null, so the caller falls back to
  // `generateDefaultSlots` either way; only the reporting differs.
  return result.kind === 'slots' ? result.days : null;
}
